import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";
import { CheckCircle, Search, RotateCcw, Eye } from "lucide-react";

interface PuzzleWorksheetProps {
  content: any;
  onProgressUpdate: (progress: number, score?: number) => void;
  showPrintView?: boolean;
}

const PuzzleWorksheet = ({ content, onProgressUpdate, showPrintView = false }: PuzzleWorksheetProps) => {
  const [foundWords, setFoundWords] = useState<Set<string>>(new Set());
  const [grid, setGrid] = useState<string[][]>([]);
  const [wordPositions, setWordPositions] = useState<Map<string, Array<{row: number, col: number}>>>(new Map());
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());
  const [highlightedWord, setHighlightedWord] = useState<string>("");
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState<{row: number, col: number} | null>(null);
  const [currentSelection, setCurrentSelection] = useState<Array<{row: number, col: number}>>([]);
  const [placedWords, setPlacedWords] = useState<string[]>([]);

  // Determine grid size based on grade level - smaller 6x6 matrix for grades 1-4
  const getGridConfig = () => {
    const gradeNum = parseInt(content.grade || "1");
    if (gradeNum <= 4) return { size: 6, maxWords: 4 };
    if (gradeNum <= 6) return { size: 8, maxWords: 5 };
    if (gradeNum <= 8) return { size: 10, maxWords: 6 };
    return { size: 12, maxWords: 8 };
  };

  useEffect(() => {
    generateWordSearchGrid();
  }, [content]);

  useEffect(() => {
    const progress = placedWords.length > 0 ? (foundWords.size / placedWords.length) * 100 : 0;
    const score = Math.round(progress);
    onProgressUpdate(progress, score);
  }, [foundWords, placedWords.length, onProgressUpdate]);

  const generateWordSearchGrid = () => {
    const { size, maxWords } = getGridConfig();
    const newGrid: string[][] = Array(size).fill(null).map(() => Array(size).fill(''));
    const positions = new Map<string, Array<{row: number, col: number}>>();
    
    // Initialize grid with empty spaces
    for (let i = 0; i < size; i++) {
      for (let j = 0; j < size; j++) {
        newGrid[i][j] = '';
      }
    }

    // Select and place words with improved algorithm
    const wordsToPlace = content.items.slice(0, maxWords);
    const successfullyPlacedWords: string[] = [];
    
    // Sort words by length (longest first) for better placement
    const sortedWords = wordsToPlace
      .map((item: any) => item.word.toUpperCase())
      .filter((word: string) => word.length >= 3 && word.length <= size - 2)
      .sort((a: string, b: string) => b.length - a.length);
    
    console.log('Attempting to place words:', sortedWords);
    
    sortedWords.forEach((word: string) => {
      let placed = false;
      let attempts = 0;
      
      while (!placed && attempts < 200) {
        const direction = Math.floor(Math.random() * 3); // 0: horizontal, 1: vertical, 2: diagonal
        let maxStartRow, maxStartCol;
        
        switch (direction) {
          case 0: // horizontal
            maxStartRow = size - 1;
            maxStartCol = size - word.length;
            break;
          case 1: // vertical
            maxStartRow = size - word.length;
            maxStartCol = size - 1;
            break;
          case 2: // diagonal
            maxStartRow = size - word.length;
            maxStartCol = size - word.length;
            break;
          default:
            maxStartRow = maxStartCol = 0;
        }
        
        if (maxStartRow >= 0 && maxStartCol >= 0) {
          const startRow = Math.floor(Math.random() * (maxStartRow + 1));
          const startCol = Math.floor(Math.random() * (maxStartCol + 1));
          
          if (canPlaceWordImproved(newGrid, word, startRow, startCol, direction)) {
            const wordPos: Array<{row: number, col: number}> = [];
            
            for (let i = 0; i < word.length; i++) {
              let row = startRow;
              let col = startCol;
              
              switch (direction) {
                case 0: col += i; break; // horizontal
                case 1: row += i; break; // vertical
                case 2: row += i; col += i; break; // diagonal
              }
              
              newGrid[row][col] = word[i];
              wordPos.push({row, col});
            }
            
            positions.set(word, wordPos);
            successfullyPlacedWords.push(word);
            placed = true;
            console.log(`Successfully placed word: ${word} at direction ${direction}`);
          }
        }
        attempts++;
      }
      
      if (!placed) {
        console.warn(`Failed to place word: ${word} after ${attempts} attempts`);
      }
    });

    // Fill empty cells with random letters, avoiding creating accidental words
    const usedLetters = successfullyPlacedWords.join('').split('');
    const letterPool = usedLetters.length > 0 ? usedLetters : 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');
    
    for (let i = 0; i < size; i++) {
      for (let j = 0; j < size; j++) {
        if (newGrid[i][j] === '') {
          newGrid[i][j] = letterPool[Math.floor(Math.random() * letterPool.length)];
        }
      }
    }

    console.log('Final grid:', newGrid);
    console.log('Successfully placed words:', successfullyPlacedWords);
    console.log('Word positions:', positions);
    
    setGrid(newGrid);
    setWordPositions(positions);
    setPlacedWords(successfullyPlacedWords);
    setFoundWords(new Set());
    setHighlightedWord("");
  };

  const canPlaceWordImproved = (grid: string[][], word: string, startRow: number, startCol: number, direction: number): boolean => {
    // Check if word fits in grid
    for (let i = 0; i < word.length; i++) {
      let row = startRow;
      let col = startCol;
      
      switch (direction) {
        case 0: col += i; break; // horizontal
        case 1: row += i; break; // vertical
        case 2: row += i; col += i; break; // diagonal
      }
      
      if (row >= grid.length || col >= grid.length || row < 0 || col < 0) {
        return false;
      }
      
      const existingLetter = grid[row][col];
      if (existingLetter !== '' && existingLetter !== word[i]) {
        return false;
      }
    }
    
    // Check for conflicts with adjacent cells (to avoid overlapping words)
    for (let i = 0; i < word.length; i++) {
      let row = startRow;
      let col = startCol;
      
      switch (direction) {
        case 0: col += i; break;
        case 1: row += i; break;
        case 2: row += i; col += i; break;
      }
      
      // Check adjacent cells for conflicts (optional - can be removed for denser grids)
      const adjacentPositions = [
        [-1, -1], [-1, 0], [-1, 1],
        [0, -1],           [0, 1],
        [1, -1],  [1, 0],  [1, 1]
      ];
      
      for (const [dr, dc] of adjacentPositions) {
        const adjRow = row + dr;
        const adjCol = col + dc;
        
        if (adjRow >= 0 && adjRow < grid.length && adjCol >= 0 && adjCol < grid.length) {
          const adjCell = grid[adjRow][adjCol];
          if (adjCell !== '' && Math.random() < 0.3) { // 30% chance to avoid conflict
            // Allow some overlap for denser grids
            continue;
          }
        }
      }
    }
    
    return true;
  };

  const handleCellMouseDown = (row: number, col: number) => {
    setIsSelecting(true);
    setSelectionStart({row, col});
    setCurrentSelection([{row, col}]);
    setSelectedCells(new Set([`${row}-${col}`]));
  };

  const handleCellMouseEnter = (row: number, col: number) => {
    if (isSelecting && selectionStart) {
      const cells = getCellsInLine(selectionStart.row, selectionStart.col, row, col);
      setCurrentSelection(cells);
      setSelectedCells(new Set(cells.map(cell => `${cell.row}-${cell.col}`)));
    }
  };

  const handleCellMouseUp = () => {
    if (isSelecting && currentSelection.length > 0) {
      checkForWords(currentSelection);
    }
    setIsSelecting(false);
    setSelectionStart(null);
    setCurrentSelection([]);
    setTimeout(() => setSelectedCells(new Set()), 200);
  };

  const getCellsInLine = (startRow: number, startCol: number, endRow: number, endCol: number) => {
    const cells: Array<{row: number, col: number}> = [];
    const deltaRow = endRow - startRow;
    const deltaCol = endCol - startCol;
    
    if (deltaRow === 0 && deltaCol === 0) {
      return [{row: startRow, col: startCol}];
    }
    
    if (deltaRow === 0) {
      // Horizontal line
      const start = Math.min(startCol, endCol);
      const end = Math.max(startCol, endCol);
      for (let col = start; col <= end; col++) {
        cells.push({row: startRow, col});
      }
    } else if (deltaCol === 0) {
      // Vertical line
      const start = Math.min(startRow, endRow);
      const end = Math.max(startRow, endRow);
      for (let row = start; row <= end; row++) {
        cells.push({row, col: startCol});
      }
    } else if (Math.abs(deltaRow) === Math.abs(deltaCol)) {
      // Diagonal line
      const steps = Math.abs(deltaRow);
      const rowStep = deltaRow > 0 ? 1 : -1;
      const colStep = deltaCol > 0 ? 1 : -1;
      
      for (let i = 0; i <= steps; i++) {
        cells.push({
          row: startRow + i * rowStep,
          col: startCol + i * colStep
        });
      }
    } else {
      // Not a straight line, return start to end positions
      const steps = Math.max(Math.abs(deltaRow), Math.abs(deltaCol));
      const rowStep = deltaRow === 0 ? 0 : deltaRow > 0 ? 1 : -1;
      const colStep = deltaCol === 0 ? 0 : deltaCol > 0 ? 1 : -1;
      
      for (let i = 0; i <= steps; i++) {
        const row = startRow + i * rowStep;
        const col = startCol + i * colStep;
        if (row >= 0 && row < grid.length && col >= 0 && col < grid.length) {
          cells.push({row, col});
        }
      }
    }
    
    return cells;
  };

  const checkForWords = (selectedPositions: Array<{row: number, col: number}>) => {
    const selectedLetters = selectedPositions.map(pos => grid[pos.row][pos.col]).join('');
    const reversedLetters = selectedLetters.split('').reverse().join('');
    
    console.log('Checking selection:', selectedLetters, 'reversed:', reversedLetters);
    console.log('Current word positions:', wordPositions);
    
    // Check against all word positions
    for (const [word, positions] of wordPositions.entries()) {
      if (!foundWords.has(word)) {
        console.log(`Checking word: ${word} with positions:`, positions);
        
        // Check if the selected positions match this word's positions exactly (forward or backward)
        const isForwardMatch = positionsMatch(selectedPositions, positions);
        const isBackwardMatch = positionsMatch(selectedPositions, [...positions].reverse());
        
        console.log(`Forward match: ${isForwardMatch}, Backward match: ${isBackwardMatch}`);
        
        if (isForwardMatch || isBackwardMatch) {
          console.log('Found word:', word);
          setFoundWords(prev => new Set([...prev, word]));
          return;
        }
      }
    }
    
    console.log('No word found for selection');
  };

  const positionsMatch = (selected: Array<{row: number, col: number}>, target: Array<{row: number, col: number}>) => {
    if (selected.length !== target.length) return false;
    
    return selected.every((pos, index) => 
      pos.row === target[index].row && pos.col === target[index].col
    );
  };

  const handleWordClick = (word: string) => {
    const upperWord = word.toUpperCase();
    if (foundWords.has(upperWord) || !wordPositions.has(upperWord)) return;
    
    // Highlight the word positions for 3 seconds
    setHighlightedWord(upperWord);
    console.log('Highlighting word:', upperWord, 'positions:', wordPositions.get(upperWord));
    setTimeout(() => setHighlightedWord(""), 3000);
  };

  const resetGame = () => {
    generateWordSearchGrid();
  };

  const getCellClassName = (row: number, col: number) => {
    const cellKey = `${row}-${col}`;
    let className = "w-8 h-8 border border-purple-300 text-xs font-bold transition-all duration-200 hover:bg-purple-100 cursor-pointer select-none flex items-center justify-center";
    
    // Adjust cell size based on grid size
    if (grid.length <= 6) {
      className = className.replace("w-8 h-8 text-xs", "w-10 h-10 text-sm");
    } else if (grid.length <= 8) {
      className = className.replace("w-8 h-8 text-xs", "w-9 h-9 text-xs");
    }
    
    // Check if cell is currently selected
    if (selectedCells.has(cellKey)) {
      className += " bg-blue-300 border-blue-500 text-blue-800";
      return className;
    }
    
    // Check if cell is part of a found word
    let isPartOfFoundWord = false;
    for (const [word, positions] of wordPositions.entries()) {
      if (foundWords.has(word) && positions.some(pos => pos.row === row && pos.col === col)) {
        className += " bg-green-200 border-green-400 text-green-800";
        isPartOfFoundWord = true;
        break;
      }
    }
    
    // Check if cell is part of highlighted word (word clicked for hint)
    if (!isPartOfFoundWord && highlightedWord && wordPositions.has(highlightedWord)) {
      const positions = wordPositions.get(highlightedWord)!;
      if (positions.some(pos => pos.row === row && pos.col === col)) {
        className += " bg-yellow-300 border-yellow-500 text-yellow-800 animate-pulse";
        return className;
      }
    }
    
    if (!isPartOfFoundWord) {
      className += " bg-white text-gray-700";
    }
    
    return className;
  };

  const { maxWords } = getGridConfig();
  const wordsToShow = content.items.slice(0, maxWords);

  // If in print view, render the printable version
  if (showPrintView) {
    return (
      <div className="print-worksheet bg-white p-6">
        <div className="text-center mb-6">
          <h2 className="text-xl font-bold mb-2">{content.title}</h2>
          <p className="text-gray-600 text-sm mb-4">{content.instructions}</p>
        </div>
        
        <div className="flex justify-center mb-6">
          <div 
            className="grid gap-1 border-2 border-gray-800 p-2"
            style={{ 
              gridTemplateColumns: `repeat(${grid.length}, minmax(0, 1fr))`,
              maxWidth: `${grid.length * 1.5}rem`
            }}
          >
            {grid.map((row, rowIndex) =>
              row.map((cell, colIndex) => (
                <div
                  key={`${rowIndex}-${colIndex}`}
                  className="w-6 h-6 border border-gray-400 flex items-center justify-center text-xs font-bold"
                >
                  {cell}
                </div>
              ))
            )}
          </div>
        </div>

        <div>
          <h3 className="text-lg font-bold mb-3">Find these words:</h3>
          <div className="grid grid-cols-3 gap-4">
            {placedWords.map((word, index) => (
              <div key={index} className="flex items-center space-x-2">
                <div className="w-4 h-4 border border-gray-400"></div>
                <span className="text-sm">{word}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 pt-4 border-t border-gray-400 text-center text-sm text-gray-600">
          <p>Student Name: ________________________  Date: ___________</p>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.5 }}
    >
      <Card className="border-2 border-purple-200">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-xl text-purple-700 flex items-center gap-2">
              <Search className="h-6 w-6" />
              {content.title}
            </CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={resetGame}
              className="text-purple-600 border-purple-300"
            >
              <RotateCcw className="h-4 w-4 mr-1" />
              Reset
            </Button>
          </div>
          <p className="text-gray-600">{content.instructions}</p>
          <p className="text-sm text-purple-600">
            Words in puzzle: {placedWords.length} | Found: {foundWords.size}
          </p>
        </CardHeader>
        
        <CardContent>
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Word Search Grid */}
            <div className="flex-1 flex justify-center">
              <div 
                className={`grid gap-1 mb-4 select-none`}
                style={{ 
                  gridTemplateColumns: `repeat(${grid.length}, minmax(0, 1fr))`,
                  maxWidth: `${grid.length * 2.5}rem`
                }}
                onMouseLeave={() => {
                  if (isSelecting) {
                    handleCellMouseUp();
                  }
                }}
              >
                {grid.map((row, rowIndex) =>
                  row.map((cell, colIndex) => (
                    <button
                      key={`${rowIndex}-${colIndex}`}
                      className={getCellClassName(rowIndex, colIndex)}
                      onMouseDown={() => handleCellMouseDown(rowIndex, colIndex)}
                      onMouseEnter={() => handleCellMouseEnter(rowIndex, colIndex)}
                      onMouseUp={handleCellMouseUp}
                    >
                      {cell}
                    </button>
                  ))
                )}
              </div>
            </div>

            {/* Word List */}
            <div className="lg:w-64">
              <h3 className="font-bold text-gray-700 mb-3">Find these words:</h3>
              <div className="space-y-2">
                {placedWords.map((word, index) => {
                  const isFound = foundWords.has(word);
                  const isHighlighted = highlightedWord === word;
                  
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.1 }}
                    >
                      <Badge
                        variant={isFound ? "default" : "outline"}
                        className={`
                          w-full justify-between p-2 transition-all duration-300 cursor-pointer
                          ${isFound 
                            ? 'bg-green-100 text-green-700 border-green-300' 
                            : 'bg-gray-50 text-gray-700 hover:bg-purple-50'
                          }
                          ${isHighlighted ? 'ring-2 ring-yellow-400 bg-yellow-50' : ''}
                        `}
                        onClick={() => handleWordClick(word)}
                      >
                        <span className={isFound ? 'line-through' : ''}>
                          {word}
                        </span>
                        <div className="flex items-center gap-1">
                          {isFound && (
                            <CheckCircle className="h-4 w-4 text-green-600" />
                          )}
                          {!isFound && (
                            <Eye className="h-3 w-3 text-gray-400" />
                          )}
                        </div>
                      </Badge>
                    </motion.div>
                  );
                })}
              </div>
              
              {placedWords.length < content.items.length && (
                <div className="mt-4 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                  <p className="text-yellow-700 text-sm">
                    <strong>Note:</strong> {content.items.length - placedWords.length} words couldn't fit in the grid. 
                    Try generating a new puzzle for different word placement.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Instructions */}
          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-blue-700 text-sm">
              <strong>How to play:</strong> Click and drag to select letters in a line (horizontal, vertical, or diagonal) to find the hidden words! 
              Click on a word in the list to see where it's hidden for 3 seconds.
            </p>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default PuzzleWorksheet;
